import { useEffect, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { validateAccountNumber } from "@/api/projectsApi";

interface CustomerData {
  payeeName: string;
  payeeAddress: string;
  payeeCity: string;
  payeeState: string;
  payeeZip: string;
}

export const usePayeeInformation = () => {
  const { control, setValue, watch } = useFormContext();
  const [storedCustomerData, setStoredCustomerData] =
    useState<CustomerData | null>(null);
  const [storedContractorData, setStoredContractorData] =
    useState<CustomerData | null>(null);

  const payeeType = useWatch({ control, name: "payeeType" });
  const customerName = useWatch({ control, name: "customerName" });
  const contractorId = useWatch({ control, name: "contractorId" });
  const contractor = useWatch({ control, name: "contractor" });
  const zipCode = useWatch({ control, name: "zipCode" });
  const accountHash = useWatch({ control, name: "accountHash" });

  const payeeFields = [
    "payeeName",
    "payeeAddress",
    "payeeCity",
    "payeeState",
    "payeeZip",
  ] as const;

  const clearPayeeFields = () => {
    payeeFields.forEach((field) => setValue(field, ""));
  };

  const storeCurrentCustomerData = () => {
    if (!storedCustomerData) {
      const currentData = payeeFields.reduce(
        (acc, field) => ({
          ...acc,
          [field]: watch(field) || "",
        }),
        {} as CustomerData
      );
      setStoredCustomerData(currentData);
    }
  };

  const storeCurrentContractorData = () => {
    if (!storedContractorData) {
      const currentData = payeeFields.reduce(
        (acc, field) => ({
          ...acc,
          [field]: watch(field) || "",
        }),
        {} as CustomerData
      );
      setStoredContractorData(currentData);
    }
  };

  const restoreCustomerData = (customerData: CustomerData) => {
    Object.entries(customerData).forEach(([field, value]) => {
      if (value) {
        setValue(field as keyof CustomerData, value);
      }
    });
  };

  const restoreContractorData = (contractorData: CustomerData) => {
    Object.entries(contractorData).forEach(([field, value]) => {
      if (value) {
        setValue(field as keyof CustomerData, value);
      }
    });
  };

  const fetchMailingDataFromSPCP = async (accountNumber: string) => {
    try {
      const response = await validateAccountNumber(accountNumber);
      const dataValues = response.data?.dataValues || response.data;

      if (dataValues) {
        setValue("payeeAddress", dataValues.Mailing_Address || "");
        setValue("payeeCity", dataValues.Mailing_City || "");
        setValue("payeeState", dataValues.Mailing_State || "");
        setValue("payeeZip", dataValues.Mailing_Zip_Code || "");
      }
    } catch (error) {
      console.warn("Could not fetch mailing address data from SPCP:", error);
    }
  };

  useEffect(() => {
    if (payeeType === "customer") {
      storeCurrentContractorData();

      const isEditFlow = watch("id");

      if (!isEditFlow) {
        setValue("payeeName", customerName);
      }

      const currentMailingAddress = watch("payeeAddress");
      const currentMailingCity = watch("payeeCity");
      const currentMailingState = watch("payeeState");
      const currentMailingZip = watch("payeeZip");
      const hasMailingData =
        currentMailingAddress ||
        currentMailingCity ||
        currentMailingState ||
        currentMailingZip;

      if (!hasMailingData && accountHash) {
        fetchMailingDataFromSPCP(accountHash);
      }
    } else if (payeeType === "contractor") {
      storeCurrentCustomerData();

      const isEditFlow = watch("id");

      if (!isEditFlow) {
        clearPayeeFields();
      }

      if (storedContractorData) {
        restoreContractorData(storedContractorData);
      }
    }
  }, [
    payeeType,
    customerName,
    contractorId,
    contractor,
    zipCode,
    accountHash,
    setValue,
    storedContractorData,
  ]);

  useEffect(() => {
    const currentPayeeName = watch("payeeName");
    const currentPayeeType = watch("payeeType");

    if (
      currentPayeeName &&
      currentPayeeType &&
      !storedCustomerData &&
      !storedContractorData
    ) {
      if (currentPayeeType === "contractor") {
        const initialContractorData = payeeFields.reduce(
          (acc, field) => ({
            ...acc,
            [field]: watch(field) || "",
          }),
          {} as CustomerData
        );
        setStoredContractorData(initialContractorData);
      } else if (currentPayeeType === "customer") {
        const initialCustomerData = payeeFields.reduce(
          (acc, field) => ({
            ...acc,
            [field]: watch(field) || "",
          }),
          {} as CustomerData
        );
        setStoredCustomerData(initialCustomerData);
      }
    }
  }, [watch, storedCustomerData, storedContractorData]);
  useEffect(() => {
    const currentPayeeName = watch("payeeName");
    const currentPayeeType = watch("payeeType");
    const currentPayeeAddress = watch("payeeAddress");
    const currentPayeeCity = watch("payeeCity");
    const currentPayeeState = watch("payeeState");
    const currentPayeeZip = watch("payeeZip");

    const hasMailingData =
      currentPayeeAddress ||
      currentPayeeCity ||
      currentPayeeState ||
      currentPayeeZip;

    const isEditFlow = watch("id");

    if (currentPayeeType && !currentPayeeName && !isEditFlow) {
      if (currentPayeeType === "customer") {
        setValue("payeeName", customerName);
      }
    }
  }, [watch, setValue, customerName, accountHash]);
};
